// src/App.js
import React, { useEffect, lazy, Suspense } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useLocation,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import CVAnalyserPage from "./pages/CVAnalyserPage";
import CVAnalysisResultsPage from "./pages/CVAnalysisResultsPage";
import PracticeInterviewQuestionsPage from "./pages/PracticeInterviewQuestionsPage";
import ForgotPassword from "./pages/forgot-password";
import ResetPassword from "./pages/reset-password";
import ProfilePage from "./pages/profile";
import PaymentHistory from "./pages/profile/PaymentHistory";
import EmployeeOnboardingPage from "./pages/EmployeeOnboardingPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import SupportContactPage from "./pages/SupportContactPage";
import PageNotFound from "./components/PageNotFound";
import Layout from "./components/layouts/Layout";
import EmployeeLayout from "./components/layouts/EmployeeLayout";
import EmployerLayout from "./components/layouts/EmployerLayout";
import { AuthProvider } from "./contexts/AuthContext";
import { ProfileInfoProvider } from "./contexts/ProfileInfoContext";
import { initGA, trackPageView } from "./analytics";
import VideoPopupManager from "./components/VideoPopupManager";
import ProfileViewNotification from "./components/ProfileViewNotification";
import MessageNotification from "./components/MessageNotification";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import "./index.css";
// Notistack
import { SnackbarProvider } from "notistack";
import PostRedirect from "./components/posts/PostRedirect";
import DoctorsJobsPage from "./pages/DoctorsJobsPage";
import JobApplicationsTracker from "./pages/job-applications-tracker";
// import SavedJobsListPage from "./pages/job-applications-tracker/components/savedJobsList";
// import EnhancedJobApplicationTracker from "./pages/EnhancedJobApplicationTracker";
import RoleSelectionPage from "./pages/RoleSelectionPage";
import EmployerOnboardingPage from "./pages/EmployerOnboardingPage";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import EmployeeCompanySearch from "./pages/EmployeeCompanySearch";
import EmployeeCompanyDetail from "./pages/EmployeeCompanyDetail";
import JobDetail from "./pages/JobDetail";
import MyProfile from "./pages/MyProfile";
import EmployerDashboard from "./pages/employer/Dashboard";
import EmployerJobs from "./pages/employer/Jobs";
import EmployerCandidates from "./pages/employer/Candidates";
import CandidateProfile from "./pages/employer/CandidateProfile";
import EmployerPipeline from "./pages/employer/Pipeline";
import HiringPipeline from "./pages/employer/HiringPipeline";
import EmployerProfile from "./pages/employer/EmployerProfile";
import EmployerLandingPage from "./pages/employer/EmployerLandingPage";
import EmployeeLandingPage from "./pages/employee/EmployeeLandingPage";
import MessagesPage from "./pages/MessagesPage";
import RecommendedMatches from "./pages/RecommendedMatches";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import ProtectedRoute from "./components/ProtectedRoute";
import PricingPage from "./pages/employer/PricingPage";
import ErrorBoundary from "./components/ErrorBoundary";

const PostsPage = lazy(() => import("./pages/PostsPage"));
const PostDetailPage = lazy(() => import("./pages/PostDetailPage"));

initGA();

const PageTracker = () => {
    const location = useLocation();

    useEffect(() => {
        trackPageView(location.pathname);
    }, [location]);

    return null;
};

function App() {
    return (
        <Router>
            <SnackbarProvider maxSnack={3}>
                <AuthProvider>
                    <ProfileInfoProvider>
                        <PageTracker />
                        {/* <VideoPopupManager /> */}
                        <ProfileViewNotification />
                        <MessageNotification />
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-lg">Loading...</div>}>
                        <Routes>
                            <Route
                                path="/employer/messages"
                                element={
                                    <EmployerLayout>
                                        <MessagesPage />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employee/messages"
                                element={
                                    <EmployeeLayout>
                                        <MessagesPage />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/"
                                element={
                                    <Layout>
                                        <DoctorsJobsPage />
                                    </Layout>
                                }
                            />
                            <Route
                                path="/employer-landing-page"
                                element={
                                    <Layout>
                                        <EmployerLandingPage />
                                    </Layout>
                                }
                            />
                            <Route
                                path="/employee-landing-page"
                                element={
                                    <Layout>
                                        <EmployeeLandingPage />
                                    </Layout>
                                }
                            />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/signup" element={<SignUpPage />} />
                            <Route path="/email-verification" element={<EmailVerificationPage />} />
                            <Route path="/role-selection" element={<RoleSelectionPage />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                            <Route
                                path="/disclaimer"
                                element={
                                    <DisclaimerPage />
                                }
                            />
                            <Route
                                path="/support"
                                element={
                                    <Layout>
                                        <SupportContactPage />
                                    </Layout>
                                }
                            />
                            <Route
                                path="/job-applications-tracker"
                                element={
                                    <Layout>
                                        <JobApplicationsTracker />
                                    </Layout>
                                }
                            />
                            <Route
                                path="/cv-analyser"
                                element={
                                    <Layout>
                                        <ErrorBoundary>
                                            <CVAnalyserPage />
                                        </ErrorBoundary>
                                    </Layout>
                                }
                            />
                            <Route
                                path="/cv-analysis-results"
                                element={
                                    <Layout>
                                        <CVAnalysisResultsPage />
                                    </Layout>
                                }
                            />
                            <Route
                                path="/practice-interview-questions"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <PracticeInterviewQuestionsPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/profile"
                                element={
                                    <Layout>
                                        <ProfilePage />
                                    </Layout>
                                }
                            />
                            <Route
                                path="/employee-profile"
                                element={<EmployeeOnboardingPage />}
                            />
                            <Route
                                path="/employee/dashboard"
                                element={
                                    <EmployeeLayout>
                                        <ErrorBoundary>
                                            <EmployeeDashboard />
                                        </ErrorBoundary>
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/company-search"
                                element={
                                    <EmployeeLayout>
                                        <ErrorBoundary>
                                            <EmployeeCompanySearch />
                                        </ErrorBoundary>
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/company/:id"
                                element={
                                    <EmployeeLayout>
                                        <EmployeeCompanyDetail />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/job/:id"
                                element={
                                    <EmployeeLayout>
                                        <JobDetail />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/job-board"
                                element={
                                    <EmployeeLayout>
                                        <DoctorsJobsPage />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/job-applications-tracker"
                                element={
                                    <EmployeeLayout>
                                        <JobApplicationsTracker />
                                    </EmployeeLayout>
                                }
                            />
                            {/* <Route
                                path="/employee/saved-jobs"
                                element={
                                    <EmployeeLayout>
                                        <SavedJobsListPage />
                                    </EmployeeLayout>
                                }
                            /> */}
                            <Route
                                path="/employee/cv-analyser"
                                element={
                                    <EmployeeLayout>
                                        <ErrorBoundary>
                                            <CVAnalyserPage />
                                        </ErrorBoundary>
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/cv-analysis-results"
                                element={
                                    <EmployeeLayout>
                                        <CVAnalysisResultsPage />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/my-profile"
                                element={
                                    <EmployeeLayout>
                                        <MyProfile />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/recommended-matches"
                                element={
                                    <EmployeeLayout>
                                        <RecommendedMatches />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employee/practice-interview-questions"
                                element={
                                    <EmployeeLayout>
                                        <PracticeInterviewQuestionsPage />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employer-profile"
                                element={<EmployerOnboardingPage />}
                            />
                            {/* Employer app */}
                            <Route
                                path="/employer/dashboard"
                                element={
                                    <EmployerLayout>
                                        <ErrorBoundary>
                                            <EmployerDashboard />
                                        </ErrorBoundary>
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/jobs"
                                element={
                                    <EmployerLayout>
                                        <ErrorBoundary>
                                            <EmployerJobs />
                                        </ErrorBoundary>
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/job-detail/:id"
                                element={
                                    <EmployerLayout>
                                        <JobDetail />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/candidates"
                                element={
                                    <EmployerLayout>
                                        <ErrorBoundary>
                                            <EmployerCandidates />
                                        </ErrorBoundary>
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/candidate/:candidateId"
                                element={
                                    <EmployerLayout>
                                        <ErrorBoundary>
                                            <CandidateProfile />
                                        </ErrorBoundary>
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/pipeline"
                                element={
                                    <EmployerLayout>
                                        <EmployerPipeline />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/hiring-pipeline"
                                element={
                                    <EmployerLayout>
                                        <HiringPipeline />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/my-profile"
                                element={
                                    <EmployerLayout>
                                        <EmployerProfile />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/pricing"
                                element={
                                    <EmployerLayout>
                                        <PricingPage />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/job-board"
                                element={
                                    <EmployerLayout>
                                        <DoctorsJobsPage />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/job-applications-tracker"
                                element={
                                    <EmployerLayout>
                                        <JobApplicationsTracker />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/cv-analyser"
                                element={
                                    <EmployerLayout>
                                        <ErrorBoundary>
                                            <CVAnalyserPage />
                                        </ErrorBoundary>
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/cv-analysis-results"
                                element={
                                    <EmployerLayout>
                                        <CVAnalysisResultsPage />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employer/account-settings"
                                element={
                                    <EmployerLayout>
                                        <ProfilePage />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employee/account-settings"
                                element={
                                    <EmployeeLayout>
                                        <ProfilePage />
                                    </EmployeeLayout>
                                }
                            />
                            <Route
                                path="/employer/payment-history"
                                element={
                                    <EmployerLayout>
                                        <PaymentHistory />
                                    </EmployerLayout>
                                }
                            />
                            <Route
                                path="/employee/payment-history"
                                element={
                                    <EmployeeLayout>
                                        <PaymentHistory />
                                    </EmployeeLayout>
                                }
                            />
                            {/* Admin Routes */}
                            <Route
                                path="/admin/hpcsa-verification"
                                element={
                                    <AdminProtectedRoute>
                                        <Layout>
                                            <AdminDashboard />
                                        </Layout>
                                    </AdminProtectedRoute>
                                }
                            />
                            {/* WordPress Posts Routes */}
                            <Route path="/blogs" element={<Layout><PostsPage /></Layout>} />
                            <Route path="/blogs/:slug" element={<Layout><PostDetailPage /></Layout>} />
                            <Route path="/blogs/id/:id" element={<Layout><PostRedirect /></Layout>} />
                            <Route
                                path="/*"
                                element={
                                    <Layout>
                                        <PageNotFound />
                                    </Layout>
                                }
                            />
                        </Routes>
                    </Suspense>
                    </ProfileInfoProvider>
                </AuthProvider>
            </SnackbarProvider>
        </Router>
    );
}

export default App;
